import type { DeviceIdScope } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/device-id-scope.enum';
import { DeviceIdScopeMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/device-id-scope.enum';
import { jwtDecode } from 'jwt-decode';
import type { AuthSource } from '@/auth/types/auth.types';
import { AuthSourceClaim, AuthSourceMap, DeviceIdScopeClaim } from '@/auth/types/auth.types';

const DeviceIdScopes: ReadonlySet<unknown> = new Set(Object.values(DeviceIdScopeMap));

/**
 * Whether a raw claim value is one of the known desk scopes.
 *
 * @param {unknown} value - The raw claim value.
 * @returns {boolean} True for a known {@link DeviceIdScope}.
 */
function isDeviceIdScope(value: unknown): value is DeviceIdScope {
  return DeviceIdScopes.has(value);
}

/**
 * Reads one claim from an ADP platform access token without verifying it.
 * Returns undefined when no token is provided or it cannot be decoded.
 *
 * @param {string | null} accessToken - The ADP platform access token.
 * @param {string} claim - The claim name to read.
 * @returns {unknown} The raw claim value, or undefined.
 */
function getClaimFromToken(accessToken: string | null, claim: string): unknown {
  if (!accessToken) {
    return undefined;
  }
  try {
    return jwtDecode<Record<string, unknown>>(accessToken)[claim];
  } catch {
    return undefined;
  }
}

/**
 * Decodes the `asrc` (auth source) integer claim from an ADP platform access token.
 * Returns undefined when no token is provided, the token cannot be decoded, or
 * the claim is absent/unrecognized — callers then fall back to the local
 * provider marker.
 *
 * @param {string | null} accessToken - The ADP platform access token.
 * @returns {AuthSource | undefined} The decoded provider, or undefined.
 */
export function getAuthSourceFromToken(accessToken: string | null): AuthSource | undefined {
  const value = getClaimFromToken(accessToken, AuthSourceClaim);
  return value === AuthSourceMap.Hanko || value === AuthSourceMap.Azure ? value : undefined;
}

/**
 * Decodes the raw `asrc` claim for diagnostics, incl. provider values the AML desk does not resolve itself.
 *
 * @param {string | null} accessToken - The ADP platform access token.
 * @returns {number | undefined} The provider number as stamped, or undefined.
 */
export function getRawAuthSourceFromToken(accessToken: string | null): number | undefined {
  const value = getClaimFromToken(accessToken, AuthSourceClaim);
  return typeof value === 'number' ? value : undefined;
}

/**
 * Decodes the `dids` (device ID scope) claim from an ADP platform access token: which desk owns the
 * session's device cookie. Undefined for tokens issued before the claim existed.
 *
 * @param {string | null} accessToken - The ADP platform access token.
 * @returns {DeviceIdScope | undefined} The desk scope, or undefined.
 */
export function getDeviceIdScopeFromToken(accessToken: string | null): DeviceIdScope | undefined {
  const value = getClaimFromToken(accessToken, DeviceIdScopeClaim);
  return isDeviceIdScope(value) ? value : undefined;
}

/**
 * Resolves the session's identity provider using the precedence:
 * 1. the `asrc` claim decoded from the ADP platform token, if present;
 * 2. otherwise the locally persisted provider marker set at login;
 * 3. otherwise `AuthSourceMap.Hanko` — the only provider the AML desk authenticates with,
 *    so a session whose origin cannot be read is treated as one of its own
 *    rather than as a provider the app can no longer talk to.
 *
 * The claim is still decoded rather than assumed: the platform continues to
 * issue `asrc: 0` for AML admin desk sessions, and an impersonated
 * session must resolve to whatever the token actually says.
 *
 * @param {string | null} accessToken - The ADP platform access token.
 * @param {AuthSource | undefined} marker - The persisted provider marker.
 * @returns {AuthSource} The resolved provider.
 */
export function resolveAuthSource(accessToken: string | null, marker: AuthSource | undefined): AuthSource {
  return getAuthSourceFromToken(accessToken) ?? marker ?? AuthSourceMap.Hanko;
}
